import { nonNullable, take, uniqueBy } from 'lib/array/arrayUtils'
import type { LoyaltyEarnTotals } from 'loyalty/hooks/useGroupedLoyaltyEarnTotals'
import { extractPkgInclusions } from 'lib/offer/inclusionUtils'
import { getOrderedPropertyInclusions } from 'components/OfferPage/Property/singlePropertyUtils'
import { bedbankPlainDescription } from 'components/AccommodationKit/roomPackageUtils'
import { stripMarkdown } from 'lib/string/stripMarkdown'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import {
  buildDisplayedPriceSummary,
  displayContextBody,
  displayContextField,
  formatOfferPageLoyaltyEarnSummary,
  nonEmptyString,
} from './offerPageContextFormatting'
import type {
  AccommodationOfferPageAssistantRoomPackageSummaryRequest,
  BuildOfferPageSurfaceContextProps,
  OfferPageAssistantPriceFormatContext,
  PropertyRoomRateViewForAssistant,
} from './offerPageContextTypes'
import { getBedbankDisplayPricing } from 'lib/hotels/bedbankPricing'

const ROOM_PACKAGE_SUMMARY_LIMIT = 8
const SUMMARY_INCLUSION_LIMIT = 5

function buildRoomPackageSummaryKey(
  summary: AccommodationOfferPageAssistantRoomPackageSummaryRequest,
): string {
  return [
    summary.roomName ?? '',
    summary.packageName ?? '',
    summary.description ?? '',
    summary.priceSummary ?? '',
    summary.loyaltyEarnSummary ?? '',
    summary.inclusions?.join('|') ?? '',
  ].join('::')
}

function inclusionDescriptions(inclusions: Array<App.OfferInclusion> | undefined): Array<string> | undefined {
  const descriptions = nonNullable(
    take(inclusions ?? [], SUMMARY_INCLUSION_LIMIT)
      .map(inclusion => displayContextField(inclusion.description)),
  )

  return descriptions.length ? descriptions : undefined
}

/** Matches `buildOfferPageSnapshot.ts`'s own LuxPlus+-first inclusion order for a flash package. */
function packageInclusionDescriptions(
  pkg: App.HotelPackage,
  offerPageState: OfferPageState,
  luxPlusInclusionsTier: App.MembershipSubscriptionTier | undefined,
): Array<string> | undefined {
  const [rawInclusions, rawBonusInclusions, luxPlusInclusions] = extractPkgInclusions(pkg, {
    checkIn: offerPageState.checkInDate,
    checkOut: offerPageState.checkOutDate,
    luxPlusTier: luxPlusInclusionsTier,
  })
  return inclusionDescriptions([...luxPlusInclusions, ...rawBonusInclusions, ...rawInclusions])
}

function buildHotelPackageSummary(
  packageView: App.OfferPackageOptionView,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
  loyaltyEarnTotals: LoyaltyEarnTotals | undefined,
  offerPageState: OfferPageState,
  luxPlusInclusionsTier: App.MembershipSubscriptionTier | undefined,
): AccommodationOfferPageAssistantRoomPackageSummaryRequest {
  return {
    roomName: displayContextField(packageView.package.roomType.name),
    packageName: displayContextField(packageView.package.name),
    description: displayContextBody(packageView.package.description),
    inclusions: packageInclusionDescriptions(packageView.package, offerPageState, luxPlusInclusionsTier),
    priceSummary: buildDisplayedPriceSummary({
      memberTotal: packageView.hotelMemberTotal,
      standardTotal: packageView.hotelTotal,
      valuedUpTo: packageView.pricing.value,
      showValuedUpTo: packageView.package.shouldDisplayValue,
    }, priceFormatContext),
    loyaltyEarnSummary: displayContextField(formatOfferPageLoyaltyEarnSummary(loyaltyEarnTotals, priceFormatContext.regionCode)),
  }
}

/**
 * Mirrors `buildOfferPageSnapshot.ts`'s own promotion-inclusion handling: an explicit (even empty)
 * `promotion.inclusions` wins over the rate's base inclusions, falling back to the promotion's
 * long-form markdown description (stripped to plain text, since the real tile renders it via
 * `MarkdownRender`) when neither is present.
 */
function bedbankInclusionDescriptions(
  rate: App.BedbankRate | undefined,
  promotion: App.BedbankPromotion | undefined,
): Array<string> | undefined {
  if (!rate) {
    return undefined
  }

  const promotionInclusions = promotion?.inclusions ?? rate.inclusions
  if (promotionInclusions.length) {
    return inclusionDescriptions(promotionInclusions)
  }

  const plainRateInclusionsLong = nonEmptyString(stripMarkdown(promotion?.rateInclusionsLong ?? ''))
  if (!plainRateInclusionsLong) {
    return undefined
  }

  const descriptions = nonNullable(
    take(plainRateInclusionsLong.split('\n'), SUMMARY_INCLUSION_LIMIT).map(displayContextField),
  )
  return descriptions.length ? descriptions : undefined
}

function buildBedbankPackageSummary(
  packageView: App.BedbankOfferPackageView,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
  loyaltyEarnTotals: LoyaltyEarnTotals | undefined,
): AccommodationOfferPageAssistantRoomPackageSummaryRequest {
  const rate = packageView.cheapestRate ?? packageView.effectiveRates[0]
  const pricing = rate ? getBedbankDisplayPricing(rate) : undefined
  const standardTotal = pricing?.total ?? packageView.package.sell?.price
  const valuedUpTo = pricing?.value ?? packageView.package.sell?.value
  const promotion = packageView.promotions[rate?.promotionId ?? '']

  return {
    roomName: displayContextField(packageView.package.name),
    description: displayContextBody(bedbankPlainDescription(packageView.package.description)),
    inclusions: bedbankInclusionDescriptions(rate, promotion),
    priceSummary: buildDisplayedPriceSummary({
      memberTotal: pricing?.memberTotal,
      standardTotal,
      valuedUpTo,
    }, priceFormatContext),
    loyaltyEarnSummary: displayContextField(formatOfferPageLoyaltyEarnSummary(loyaltyEarnTotals, priceFormatContext.regionCode)),
  }
}

function buildPropertyRoomRateSummary(
  roomRateView: PropertyRoomRateViewForAssistant,
  priceFormatContext: OfferPageAssistantPriceFormatContext,
  loyaltyEarnTotals: LoyaltyEarnTotals | undefined,
): AccommodationOfferPageAssistantRoomPackageSummaryRequest {
  const rate = roomRateView.rates[0]
  // Matches `buildOfferPageSnapshot.ts`'s own inclusion order (LuxPlus+ > bonus > bundle > base)
  // instead of a single flat list -- `shouldShowInclusionsValidityText`'s LPC-package-matching
  // stripping is deliberately not replicated here, since this is a cosmetic chat-title summary, not
  // the turn-grounding snapshot that stripping exists to keep accurate.
  const inclusions = rate
    ? getOrderedPropertyInclusions({
        luxPlusInclusions: rate.luxPlusInclusions ?? [],
        bonusInclusions: rate.bonusInclusions ?? [],
        bundleInclusions: rate.bundleInclusions ?? [],
        inclusions: rate.inclusions ?? [],
      })
    : roomRateView.propertyRoom.inclusions
  return {
    roomName: displayContextField(roomRateView.propertyRoom.name),
    packageName: displayContextField(rate?.name),
    description: displayContextBody(roomRateView.propertyRoom.description),
    inclusions: inclusionDescriptions(inclusions),
    priceSummary: buildDisplayedPriceSummary({
      memberTotal: rate?.luxPlusTotals?.displayPrice,
      standardTotal: rate?.totals.displayPrice,
      valuedUpTo: rate?.totals.valuedUpToPrice,
    }, priceFormatContext),
    loyaltyEarnSummary: displayContextField(formatOfferPageLoyaltyEarnSummary(loyaltyEarnTotals, priceFormatContext.regionCode)),
  }
}

export function buildRoomPackageSummaries({
  offerPageState,
  hotelPackageViews = [],
  bedbankPackageViews = [],
  propertyRoomRateViews = [],
  roomPackageLoyaltyEarnTotals = {},
  priceFormatContext,
  luxPlusInclusionsTier,
}: Pick<BuildOfferPageSurfaceContextProps, 'offerPageState' | 'hotelPackageViews' | 'bedbankPackageViews' | 'propertyRoomRateViews' | 'roomPackageLoyaltyEarnTotals' | 'priceFormatContext' | 'luxPlusInclusionsTier'>): Array<AccommodationOfferPageAssistantRoomPackageSummaryRequest> {
  // This summary is one row per room (not per rate), so it uses the room's first rate's total --
  // matching this function's pre-existing room-level granularity; per-rate loyalty is only
  // surfaced by the newer snapshot-based builder, not this legacy summary.
  const propertySummaries = uniqueBy(
    propertyRoomRateViews.map(roomRateView => buildPropertyRoomRateSummary(roomRateView, priceFormatContext, roomPackageLoyaltyEarnTotals[roomRateView.rates[0]?.rateId ?? ''])),
    buildRoomPackageSummaryKey,
  )
  if (propertySummaries.length) {
    return take(propertySummaries, ROOM_PACKAGE_SUMMARY_LIMIT)
  }

  const hotelSummaries = uniqueBy(
    hotelPackageViews.map(packageView => buildHotelPackageSummary(packageView, priceFormatContext, roomPackageLoyaltyEarnTotals[packageView.package.uniqueKey], offerPageState, luxPlusInclusionsTier)),
    buildRoomPackageSummaryKey,
  )
  if (hotelSummaries.length) {
    return take(hotelSummaries, ROOM_PACKAGE_SUMMARY_LIMIT)
  }

  return take(
    uniqueBy(
      bedbankPackageViews.map(packageView => buildBedbankPackageSummary(packageView, priceFormatContext, roomPackageLoyaltyEarnTotals[(packageView.cheapestRate ?? packageView.effectiveRates[0])?.id ?? ''])),
      buildRoomPackageSummaryKey,
    ),
    ROOM_PACKAGE_SUMMARY_LIMIT,
  )
}
